import { useState, useEffect, ReactNode } from 'react';
import { User, Session, isAuthRetryableFetchError } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { AuthContext } from '@/contexts/authContext';
import { isSameUser } from '@/lib/authIdentity';
import { usePostHog } from 'posthog-js/react';

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const posthog = usePostHog();

  useEffect(() => {
    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        // Referenz behalten, solange es dieselbe Person ist -- sonst loest
        // jede Token-Erneuerung (z.B. beim Zurueckwechseln auf den Tab) eine
        // komplette Neuinitialisierung der App aus. Siehe lib/authIdentity.
        setUser(previous => (isSameUser(previous, session?.user ?? null)
          ? previous
          : session?.user ?? null));
        setLoading(false);

        // Handle PostHog identification
        if (event === 'SIGNED_IN' && session?.user) {
          // Identify user in PostHog when they sign in
          posthog?.identify(session.user.id, {
            email: session.user.email,
            name: `${session.user.user_metadata?.first_name || ''} ${session.user.user_metadata?.last_name || ''}`.trim() || undefined
          });
        } else if (event === 'SIGNED_OUT') {
          // Reset PostHog when user signs out
          posthog?.reset();
        }
      }
    );

    // Get initial session
    supabase.auth.getSession().then(({ data: { session }, error }) => {
      if (error) {
        // Network/server hiccup (Supabase 5xx, Cloudflare 522, offline, timeout):
        // token is still valid — keep the local session so users stay logged in
        // once the backend recovers. Do NOT clear local auth here.
        if (isAuthRetryableFetchError(error)) {
          console.warn('Transient auth fetch error, keeping local session:', error);
          setLoading(false);
          return;
        }

        // Real auth error (invalid/expired token, 401/403) — safe to clear.
        console.warn('Auth error, clearing local auth:', error);
        Object.keys(localStorage).forEach(key => {
          if (key.startsWith('sb-') && key.includes('-auth-token')) {
            localStorage.removeItem(key);
          }
        });
        sessionStorage.clear();

        setSession(null);
        setUser(null);
        setLoading(false);
        return;
      }

      setSession(session);
      setUser(previous => (isSameUser(previous, session?.user ?? null)
        ? previous
        : session?.user ?? null));
      setLoading(false);

      // Identify user in PostHog if already logged in
      if (session?.user) {
        posthog?.identify(session.user.id, {
          email: session.user.email,
          name: `${session.user.user_metadata?.first_name || ''} ${session.user.user_metadata?.last_name || ''}`.trim() || undefined
        });
      }
    });

    return () => subscription.unsubscribe();
  }, [posthog]);

  const signUp = async (email: string, password: string, firstName?: string, lastName?: string) => {
    const redirectUrl = `${window.location.origin}/`;
    
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          first_name: firstName,
          last_name: lastName,
        }
      }
    });
    
    return { error };
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    
    return { error };
  };

  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut({ scope: 'local' });

      // If we get a 403 or any error, force clear the session locally
      if (error) {
        console.warn('Logout error, forcing local cleanup:', error);

        // Clear all auth-related storage
        Object.keys(localStorage).forEach(key => {
          if (key.startsWith('sb-') && key.includes('-auth-token')) {
            localStorage.removeItem(key);
          }
        });
        sessionStorage.clear();

        // Clear cookies manually
        document.cookie.split(";").forEach((c) => {
          const eqPos = c.indexOf("=");
          const name = eqPos > -1 ? c.substr(0, eqPos).trim() : c.trim();
          // Clear auth-related cookies
          if (name.includes('sb-') || name.includes('auth')) {
            const hostname = window.location.hostname;
            document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
            document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${hostname}`;
            // Also clear for parent domain if it's a subdomain
            if (hostname.includes('.')) {
              const parentDomain = hostname.split('.').slice(-2).join('.');
              document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=.${parentDomain}`;
            }
          }
        });

        // Force state update
        setUser(null);
        setSession(null);

        // Still reset PostHog
        posthog?.reset();

        // Return success since we cleaned up locally
        return { error: null };
      }

      // PostHog reset is also handled in the auth state change listener,
      // but we call it here too for immediate cleanup
      posthog?.reset();
      return { error };
    } catch (err) {
      console.error('Unexpected logout error:', err);

      // Force cleanup on any unexpected error
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('sb-') && key.includes('-auth-token')) {
          localStorage.removeItem(key);
        }
      });
      sessionStorage.clear();

      // Clear cookies
      document.cookie.split(";").forEach((c) => {
        const eqPos = c.indexOf("=");
        const name = eqPos > -1 ? c.substr(0, eqPos).trim() : c.trim();
        if (name.includes('sb-') || name.includes('auth')) {
          const hostname = window.location.hostname;
          document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
          document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${hostname}`;
          // Also clear for parent domain if it's a subdomain
          if (hostname.includes('.')) {
            const parentDomain = hostname.split('.').slice(-2).join('.');
            document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=.${parentDomain}`;
          }
        }
      });

      setUser(null);
      setSession(null);
      posthog?.reset();

      return { error: null };
    }
  };

  return (
    <AuthContext.Provider value={{
      user,
      session,
      loading,
      signUp,
      signIn,
      signOut,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

